import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from './api';

export type Me = {
  user_id: string;
  email: string;
  role: 'buyer' | 'seller' | 'admin';
  seller_status: 'none' | 'pending' | 'approved' | 'rejected' | 'suspended';
  stripe_account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  connect_details_submitted: boolean;
  seller_application: { status: string; admin_note: string | null } | null;
};

type MeContextValue = { me: Me | null; loading: boolean; refresh: () => Promise<void> };

const MeContext = createContext<MeContextValue>({
  me: null,
  loading: true,
  refresh: async () => {},
});

/** Fetches the current user's profile once and shares it across the app. */
export function MeProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const api = useApi();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isSignedIn) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await api<Me>('/me'));
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, api]);

  useEffect(() => {
    if (isLoaded) refresh();
  }, [isLoaded, isSignedIn]);

  return <MeContext.Provider value={{ me, loading, refresh }}>{children}</MeContext.Provider>;
}

export function useMe() {
  return useContext(MeContext);
}
